import { DatePicker, Grid, GridColumn, GridRow } from '@bp/ui-components';
import { graphql } from 'client/gql';
import { AbsenceEvaluationClassMembersTable } from 'components/Absences/tables/AbsenceEvaluationClassMembersTable';
import { BpPage } from 'components/BpPage/BpPage';
import { OrganizationConfigContext } from 'context/OrganizationConfigContext';
import dayjs from 'dayjs';
import { useAuthClaims } from 'hooks/useAuthClaims';
import { usePermissionChecker } from 'hooks/usePermissionChecker';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from 'urql';
import { useIsDesktop } from '../../../hooks/useIsDesktop';

const AbsencesEvaluationClassSubpageQuery = graphql(`
  query AbsencesEvaluationClassSubpageData($classesWhere: ClassWhere, $from: Date!, $to: Date!, $classUuid: ID!) {
    classes(where: $classesWhere) {
      __typename
      uuid
      name
    }
    ...AbsenceEvaluationClassMembersTableFields
  }
`);

export const AbsencesEvaluationClassSubpage = () => {
  const { t } = useTranslation();
  const { pimAuthClaims } = useAuthClaims();
  const isDesktop = useIsDesktop();
  const organization = pimAuthClaims.getOrganization();
  const { currentSchoolYear } = useContext(OrganizationConfigContext);
  const { classUuid } = useParams();
  const navigate = useNavigate();

  const startDate = dayjs(currentSchoolYear.start).toDate();
  const endDate = dayjs(currentSchoolYear.end).toDate();

  const [fromDate, setFromDate] = useState<Date>(startDate);
  const [toDate, setToDate] = useState<Date>(endDate);

  const [{ data }] = useQuery({
    query: AbsencesEvaluationClassSubpageQuery,
    variables: {
      classesWhere: {
        uuid: classUuid,
        organization: { uuid: organization.uuid },
      },
      from: fromDate,
      to: toDate,
      classUuid: classUuid ?? '',
    },
    pause: !classUuid,
  });

  const selectedClass = data?.classes[0] ?? null;

  const permissions = usePermissionChecker();

  return (
    <BpPage
      hideBreadcrumbs={!isDesktop}
      hideHeader={!isDesktop}
      title={t('absences.evaluation.title', { count: 1 })}
      subheadline={selectedClass?.name ?? ''}
      isForbidden={!permissions?.canViewAbsencesEvaluation()}
      showBackButton
    >
      <Grid useFormGap className='mb-4'>
        <GridRow>
          <GridColumn width={2}>
            <DatePicker
              onChange={(value) => setFromDate(value ?? new Date())}
              name='from'
              label={t('common.start')}
              value={fromDate}
              maxDate={toDate}
            />
          </GridColumn>
          <GridColumn width={2}>
            <DatePicker
              onChange={(value) => setToDate(value ?? new Date())}
              name='to'
              label={t('common.end')}
              value={toDate}
              minDate={fromDate}
            />
          </GridColumn>
        </GridRow>
      </Grid>

      <AbsenceEvaluationClassMembersTable onNavigate={(memberUuid) => navigate(memberUuid)} query={data} />
    </BpPage>
  );
};
